import { useEffect, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { Block } from '@/types';
import { A4_LANDSCAPE } from '@/constants/pageFormat';
import { blocksToTiptapDoc } from '@/editor/blockConversion';
import { blockNodeExtensions } from '@/editor/nodes';
import { sectionContentExtensions } from '@/editor/sectionContentExtensions';
import { InvestmentAutoCalc } from '@/editor/extensions/investmentAutoCalc';
import { InvestmentMoneyBlur } from '@/editor/extensions/investmentMoneyBlur';
import { InvestmentRowDelete } from '@/editor/extensions/investmentRowDelete';
import { InvestmentRowLink } from '@/editor/extensions/investmentRowLink';
import { InvestmentRowVersion } from '@/editor/extensions/investmentRowVersion';
import { InvestmentCellShortcuts } from '@/editor/extensions/investmentCellShortcuts';
import { InvestmentColumnResize } from '@/editor/extensions/investmentColumnResize';
import { InvestmentHiddenHours } from '@/editor/extensions/investmentHiddenHours';
import { useQuoteDocumentMode } from './QuoteDocument';
import SheetGaps from './SheetGaps';
import { SheetLayoutProvider } from './SheetLayoutContext';
import { SheetPagination, type SheetMode } from './sheetPagination';
import { sheetStyles } from './sheetStyles';
import { useSheetLayout } from './useSheetLayout';

interface TiptapQuoteDocumentProps {
  blocks: Block[];
  variablesMap?: Record<string, string>;
  renderHash: string;
}

/**
 * Alleen-lezen variant van hetzelfde Tiptap-document dat de builder gebruikt.
 * Dit is de enige renderer voor de klantlink én de PDF: de PDF-export opent
 * deze weergave met `?print=1`. Lange blokken worden expliciet over vaste
 * A4-vellen verdeeld (`paginateDocument`); online staan die vellen los van
 * elkaar met een tussenruimte, in de PDF is elk vel precies één pagina.
 */
export default function TiptapQuoteDocument({
  blocks,
  variablesMap,
  renderHash,
}: TiptapQuoteDocumentProps) {
  const mode: SheetMode = useQuoteDocumentMode() === 'print' ? 'print' : 'web';
  const content = useMemo(() => blocksToTiptapDoc(blocks), [blocks]);

  const editor = useEditor({
    editable: false,
    extensions: [
      ...sectionContentExtensions,
      InvestmentColumnResize,
      InvestmentRowDelete,
      InvestmentRowLink,
      InvestmentRowVersion,
      InvestmentCellShortcuts,
      InvestmentAutoCalc,
      InvestmentMoneyBlur,
      InvestmentHiddenHours,
      ...blockNodeExtensions,
      SheetPagination,
    ],
    content,
    editorProps: {
      attributes: {
        class: 'tiptap-editor shared-tiptap-renderer max-w-none focus:outline-none',
        'aria-label': 'Offertedocument',
      },
      // Klikken selecteert geen blokken: de klant leest, tekent en accepteert alleen.
      handleDOMEvents: {
        mousedown: () => true,
      },
    },
  });

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.commands.setContent(content, { emitUpdate: false });
  }, [content, editor]);

  useEffect(() => {
    if (!editor || !variablesMap) return;
    editor.commands.setVariablesMap(variablesMap);
  }, [editor, variablesMap]);

  const { layout, ready: layoutReady } = useSheetLayout(editor, mode, renderHash);

  useEffect(() => {
    if (!editor) return;
    const root = editor.view.dom as HTMLElement;

    const openLinksInNewTab = (event: MouseEvent) => {
      const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (mode !== 'web' || !anchor || !root.contains(anchor)) return;
      event.preventDefault();
      window.open(anchor.href, '_blank', 'noopener,noreferrer');
    };
    root.addEventListener('click', openLinksInNewTab);
    return () => root.removeEventListener('click', openLinksInNewTab);
  }, [editor, mode]);

  if (!editor) return null;

  const width = A4_LANDSCAPE.width;
  const height = A4_LANDSCAPE.height;

  return (
    <div
      className="shared-tiptap-print-document"
      data-quote-content
      data-sheet-mode={mode}
      data-print-layout-ready={layoutReady ? 'true' : 'false'}
      data-print-page-count={layout?.sheetCount ?? 0}
      data-render-hash={renderHash}
      style={{ width, position: 'relative' }}
    >
      {mode === 'print' ? (
        <style>{`
          html,
          body,
          #root {
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
          }

          html::-webkit-scrollbar,
          body::-webkit-scrollbar,
          #root::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
        `}</style>
      ) : null}
      <style>{sheetStyles('.shared-tiptap-print-document')}</style>
      <style>{`
        .shared-tiptap-print-document,
        .shared-tiptap-print-document .ProseMirror {
          width: ${width}px !important;
          max-width: ${width}px !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .shared-tiptap-print-document[data-sheet-mode='print'],
        .shared-tiptap-print-document[data-sheet-mode='print'] .ProseMirror {
          background: #ffffff;
        }

        .shared-tiptap-print-document[data-sheet-mode='print'] .ProseMirror > .react-renderer {
          break-after: page;
          page-break-after: always;
        }

        .shared-tiptap-print-document[data-sheet-mode='print'] .ProseMirror > .react-renderer:last-of-type {
          break-after: auto;
          page-break-after: auto;
        }

        .shared-tiptap-print-document .ProseMirror > p:last-child {
          display: none !important;
        }

        .shared-tiptap-print-document .block-node-view,
        .shared-tiptap-print-document .section-node-view,
        .shared-tiptap-print-document .investment-group-view,
        .shared-tiptap-print-document .ProseMirror-selectednode {
          outline: none !important;
          box-shadow: none !important;
          border-radius: 0 !important;
        }

        .shared-tiptap-print-document .section-node-view > div {
          overflow: clip !important;
        }

        /*
         * Een niet-ingevulde variabele is in de builder een blauwe chip. Voor de
         * klant en in de PDF is het gewone tekst, ook leesbaar op gekleurde pagina's.
         */
        .shared-tiptap-print-document .variable-chip {
          background: transparent !important;
          border: 0 !important;
          padding: 0 !important;
          color: inherit !important;
          font-size: inherit !important;
          font-weight: inherit !important;
        }

        /*
         * Een blok is 5 mm lager dan het vel (A4_LANDSCAPE.contentHeight). Het
         * vel beslaat wel de volle A4-hoogte; vul die marge visueel met de
         * pagina-achtergrond. Absolute plaatsing verandert de layout niet.
         */
        .shared-tiptap-print-document .block-node-view[data-full-page='true'],
        .shared-tiptap-print-document .section-node-view,
        .shared-tiptap-print-document .investment-group-view {
          position: relative !important;
          overflow: visible !important;
        }

        .shared-tiptap-print-document .block-node-view[data-full-page='true'] > * {
          position: relative;
          z-index: 1;
          overflow: clip;
        }

        .shared-tiptap-print-document .block-node-view[data-full-bleed-image] > * {
          opacity: 0 !important;
        }

        .shared-tiptap-print-document .block-node-view[data-full-bleed-image]::before {
          display: none;
        }

        .shared-tiptap-print-document .section-node-view > div,
        .shared-tiptap-print-document .investment-group-view > div {
          position: relative;
          z-index: 1;
        }

        .shared-tiptap-print-document .block-node-view[data-full-page='true']::before,
        .shared-tiptap-print-document .section-node-view::before,
        .shared-tiptap-print-document .investment-group-view::before {
          position: absolute;
          z-index: 0;
          top: 0;
          right: 0;
          left: 0;
          height: ${height}px;
          content: '';
          pointer-events: none;
          background-color: inherit;
          background-image: inherit;
          background-position: center bottom;
          background-size: ${width}px ${height}px;
        }

        @media print {
          html, body, #root {
            width: ${width}px !important;
            max-width: ${width}px !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }

          .shared-tiptap-print-document,
          .shared-tiptap-print-document * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
      <SheetLayoutProvider value={layout}>
        <EditorContent editor={editor} />
      </SheetLayoutProvider>
      {mode === 'web' && layout ? <SheetGaps layout={layout} /> : null}
    </div>
  );
}
